import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Paper,
    Typography,
    Checkbox,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    InputAdornment,
    Collapse,
} from "@mui/material";

import {
    Search,
    ExpandMore,
    PlayArrow,
    MoreVert,
    ZoomIn,
    Visibility,
    Delete,
    Menu as MenuIcon,
} from "@mui/icons-material";

import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CircleTwoToneIcon from '@mui/icons-material/CircleTwoTone';
import NavigationIcon from '@mui/icons-material/Navigation';
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CircleIcon from '@mui/icons-material/Circle';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';


import { CircularProgress } from "@mui/material";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import GeoJSON from "ol/format/GeoJSON";
import { bbox as bboxStrategy } from "ol/loadingstrategy";
import Overlay from 'ol/Overlay';


// Import Openlayer Library //
import "ol/ol.css";
import { Map, View } from "ol";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import VideoPlayer from "./components/videoPlayer";
import { get_tickets_url, video_streaming_url } from "./api_registry";
import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import CircleStyle from 'ol/style/Circle';
import { internal_createExtendSxProp } from "@mui/material/zero-styled";


export default function VideoManager() {
    const driveMapInstanceRef = useRef(null);
    const driveMapRef = useRef(null);
    const overlayRef = useRef(null);
    const iconRef = useRef(null);
    const overviewMapInstanceRef = useRef(null);
    const overlayRef1 = useRef(null);
    const iconRef1 = useRef(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedSegment, setSelectedSegment] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(true);
    const overviewMapRef = useRef(null);
    const [videourl, setVideourl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [ticket, setTicket] = useState(null);

    // Right panel width (px)
    const [rightWidth, setRightWidth] = useState(360);
    const rightResizingRef = useRef(false);

    const [projects, setProjects] = useState([]);

    const createWFSLayer = (layer) => {
        const baseUrl = layer.wfs_url.split("&bbox=")[0]; // clean base URL

        const vectorSource = new VectorSource({
            format: new GeoJSON(),
            strategy: bboxStrategy,
            url: (extent) => {
                const bboxStr = extent.join(",");
                return `${baseUrl}&bbox=${bboxStr}`;
            },
        });

        return new VectorLayer({
            source: vectorSource,
            style: function (feature) {
                const geometryType = feature.getGeometry().getType();

                switch (geometryType) {
                    case 'Point':
                    case 'MultiPoint':
                        return new Style({
                            image: new CircleStyle({
                                radius: 6,
                                fill: new Fill({ color: '#23b20d' }),
                                stroke: new Stroke({ color: '#b8af01', width: 2 }),
                            }),
                        });

                    case 'LineString':
                    case 'MultiLineString':
                        return new Style({
                            stroke: new Stroke({
                                color: '#ffbf00',
                                width: 2,
                            }),
                        });

                    case 'Polygon':
                    case 'MultiPolygon':
                        return new Style({
                            stroke: new Stroke({
                                color: '#000606',
                                width: 2,
                            }),
                            fill: new Fill({
                                color: 'rgba(0, 255, 242, 0.3)', // semi-transparent fill
                            }),
                        });

                    default:
                        return new Style({
                            stroke: new Stroke({ color: '#FF0000', width: 2 }),
                        });
                }
            },
        });

    };

    // get video url
    const getManifestUrl = async (segment) => {
        try {
            setLoading(true);
            setTicket(segment);

            const res = await fetch(`${video_streaming_url}/${segment.id}/video-playlist`, {
                headers: {
                    accept: "application/json",
                    "accept-language": "en-US,en;q=0.9,en-IN;q=0.8",
                    "cache-control": "no-cache",
                    pragma: "no-cache",
                },
                method: "GET",
                mode: "cors",
                credentials: "omit",
            });

            if (!res.ok) {
                throw new Error(`HTTP error! Status: ${res.status}`);
            }

            const data = await res.json();

            if (data.statusCode === 200) {
                const manifest_url = JSON.parse(data.data).manifest_url;
                if (manifest_url) {
                    setVideourl(manifest_url);
                } else {
                    alert("Video is not available for this ticket.");
                    setVideourl(null);
                }
            } else {
                alert(`Error: ${data.details || "Unable to fetch manifest"}`);
                setVideourl(null);
            }
        } catch (err) {
            console.error(err);
            alert(`Failed to fetch video: ${err.message}`);
            setVideourl(null);
        } finally {
            setLoading(false);
        }
    };

    // load projects and tickets
    useEffect(() => {
        const fetchTickets = async () => {
            const params = new URLSearchParams({
                page: 1,
                page_size: 500,
                sort: "created_at:desc",
            });

            const res = await fetch(`${get_tickets_url}?${params.toString()}`);
            const data = await res.json();

            const segments = {};
            const projectsMap = {};

            for (const item of data.items) {
                if (!segments[item.project_id]) {
                    segments[item.project_id] = [];
                }
                segments[item.project_id].push({
                    id: item.ticket_id,
                    name: item.ticket_name,
                    checked: false,
                });

                projectsMap[item.project_id] = item.project_name;
            }

            const structured = Object.keys(projectsMap).map((k) => ({
                id: Number(k),
                name: projectsMap[k],
                expanded: false,
                checked: false,
                segments: segments[k] || [],
            }));

            setProjects(structured);
        };

        fetchTickets();
    }, []);


    // Update marker + map center + map rotation
    function updateMarker(data) {

        // const { lat, lon, bearing } = JSON.parse(data);
        const json_data = JSON.parse(data);
        const lat = json_data['lat']
        const lon = json_data['lon']
        const bearing = json_data['bearing']


        const map = driveMapInstanceRef.current;
        const overlay = overlayRef.current;
        const overlay1 = overlayRef1.current;

        if (!map || !overlay || !overlay1) return;

        const newPos = [lon, lat];
        const newRotation = (-bearing * Math.PI) / 180;

        // Animate map center + rotation
        map.getView().animate({
            center: newPos,
            rotation: newRotation,
            duration: 300
        });

        // Move overlay to new position
        overlay.setPosition(newPos);
        overlay1.setPosition(newPos);
    }


    // one time map instance init
    useEffect(() => {
        if (!driveMapInstanceRef.current) {
            driveMapInstanceRef.current = new Map({
                target: driveMapRef.current,
                layers: [new TileLayer({ source: new OSM() })],
                view: new View({
                    projection: "EPSG:4326",
                    center: [87.38, 21.91],
                    zoom: 18,
                }),
            });

            if (!overlayRef.current) {
                const overlay = new Overlay({
                    position: [77.5946, 12.9716], // initial coords 
                    element: iconRef.current,
                    positioning: 'center-center'
                });
                driveMapInstanceRef.current.addOverlay(overlay);
                overlayRef.current = overlay;
            }
        }

        if (!overviewMapInstanceRef.current) {
            overviewMapInstanceRef.current = new Map({
                target: overviewMapRef.current, // <-- FIXED!
                layers: [new TileLayer({ source: new OSM() })],
                view: new View({
                    projection: "EPSG:4326",
                    center: [87.38, 21.91],
                    zoom: 12,
                }),
            });

            if (!overlayRef1.current) {
                const overlay = new Overlay({
                    position: [77.5946, 12.9716], // initial coords 
                    element: iconRef1.current,
                    positioning: 'center-center'
                });
                overviewMapInstanceRef.current.addOverlay(overlay);
                overlayRef1.current = overlay;
            }
        }
    }, []);

    // on select ticket load all layers
    useEffect(() => {
        if (!overviewMapInstanceRef.current || !ticket) return;

        fetch(
            `https://app.mapgeoid.com/api-service/ticket-management/wfs-layers?ticket_id=${ticket.id}`,
            {
                method: "GET",
                credentials: "include",
            },
        )
            .then((res) => res.json())
            .then((layers) => {
                layers.forEach((layer) => {
                    const wfsLayer = createWFSLayer(layer);
                    overviewMapInstanceRef.current.addLayer(wfsLayer);

                    if (layer.type === "route" && layer.bbox) {
                        const { min_x, min_y, max_x, max_y } = layer.bbox;
                        const extent = [min_x, min_y, max_x, max_y];

                        overviewMapInstanceRef.current.getView().fit(extent, {
                            duration: 1000, // smooth animation
                            padding: [50, 50, 50, 50], // optional padding around the extent
                        });
                    }
                });

            })
            .catch(console.error);
    }, [overviewMapInstanceRef.current, ticket]);

    // Ensure map always targets the bottom-right map container and updates on rightWidth changes
    useEffect(() => {
        const map = driveMapInstanceRef.current;
        if (!map) return;

        const target = driveMapRef.current;

        if (target) {
            map.setTarget(target);
            // REQUIRED after resize
            // setTimeout(() => map.updateSize(), 0);
            // setTimeout(() => {driveMapInstanceRef.current?.updateSize();}, 200);
            setTimeout(() => {
                driveMapInstanceRef.current?.updateSize();
                overviewMapInstanceRef.current?.updateSize();
            }, 300);

        }
    }, [rightWidth]);

    // Add mouse handlers for resizing the right panel
    useEffect(() => {

        function onMove(e) {
            if (!rightResizingRef.current) return;

            const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
            if (clientX == null) return;

            const newRightWidth = Math.max(
                200,
                window.innerWidth - clientX - 8
            );

            const maxW = Math.max(300, window.innerWidth - 300);

            setRightWidth(Math.min(newRightWidth, maxW));
        }

        function onUp() {
            rightResizingRef.current = false;
            document.body.style.userSelect = "auto";
        }

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        window.addEventListener("touchmove", onMove);
        window.addEventListener("touchend", onUp);

        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("touchend", onUp);
        };

    }, []);

    const handleMenuOpen = (event, segment) => {
        setAnchorEl(event.currentTarget);
        setSelectedVideo(segment);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedVideo(null);
    };

    const handleZoomToVideo = () => {
        console.log("Zoom to video:", selectedVideo);
        handleMenuClose();
    };

    const handleShowDetails = () => {
        console.log("Show details:", selectedVideo);
        handleMenuClose();
    };

    const handleDeleteVideo = () => {
        console.log("Delete video:", selectedVideo);
        handleMenuClose();
    };

    const toggleProject = (projectId) => {
        setProjects(
            projects.map((p) =>
                p.id === projectId ? { ...p, expanded: !p.expanded } : p,
            ),
        );
    };

    const toggleProjectCheck = (projectId) => {
        setProjects(
            projects.map((p) =>
                p.id === projectId ? { ...p, checked: !p.checked } : p,
            ),
        );
    };

    // Right panel is permanent; resizing handled via drag handle below

    const getSelectedVideoInfo = () => {
        for (const project of projects) {
            const segment = project.segments.find(
                (s) => s.id === selectedSegment,
            );
            if (segment) {
                return `${project.name} - ${segment.name}`;
            }
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                // gap: 1,
                height: "100vh",
                width: "100vw",
                overflow: "hidden",
                p: 1,
                boxSizing: "border-box",
                // position: "relative",
            }}
        >
            {/* Left Sidebar - Drawer */}
            <Paper
                variant="outlined"
                sx={{
                    width: drawerOpen ? "25%" : "0%",
                    display: "flex",
                    flexDirection: "column",
                    borderWidth: drawerOpen ? 2 : 0,
                    flexShrink: 0,
                    overflow: "hidden",
                    transition: "width 0.3s ease-in-out",
                    opacity: drawerOpen ? 1 : 0,
                    marginRight: drawerOpen ? "0.25rem" : 0,
                }}
            >
                {drawerOpen && (
                    <>
                        <Box
                            sx={{
                                p: 1,
                                borderBottom: 1,
                                borderColor: "divider",
                                flexShrink: 0,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                height: "28px",
                            }}
                        >
                            <Typography variant="h6" fontWeight="600">
                                Videos
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => setDrawerOpen(false)}
                            >
                                <KeyboardArrowLeftIcon />
                            </IconButton>
                        </Box>

                        {/* Search Bar */}
                        <Box sx={{ p: 1, flexShrink: 0 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search fontSize="small" />
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            borderRadius: 3,
                                        },
                                    },
                                }}
                            />
                        </Box>

                        {/* Projects List - Scrollable */}
                        <Box sx={{ flex: 1, overflow: "auto", px: 1, pb: 1 }}>
                            {projects.map((project) => (
                                <Box key={project.id} sx={{ mb: 1 }}>
                                    {/* Project Header */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            p: 1,
                                            bgcolor: "grey.100",
                                            borderRadius: 1,
                                            cursor: "pointer",
                                            "&:hover": {
                                                bgcolor: "grey.200",
                                            },
                                        }}
                                        onClick={() =>
                                            toggleProject(project.id)
                                        }
                                    >
                                        <Checkbox
                                            checked={project.checked}
                                            size="small"
                                            sx={{ p: 0, mr: 1 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleProjectCheck(project.id);
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{ flex: 1, fontWeight: 500 }}
                                        >
                                            {project.name}
                                        </Typography>
                                        <ExpandMore
                                            sx={{
                                                transform: project.expanded
                                                    ? "rotate(180deg)"
                                                    : "rotate(0deg)",
                                                transition: "transform 0.3s",
                                                color: "primary.main",
                                            }}
                                        />
                                    </Box>

                                    {/* Video Segments - Collapsible */}
                                    <Collapse in={project.expanded}>
                                        <Box
                                            sx={{
                                                mt: 1,
                                                ml: 1,
                                                maxHeight: 500,
                                                overflow: "auto",
                                            }}
                                        >
                                            {project.segments.map((segment) => (
                                                <Box
                                                    key={segment.id}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        p: 1,
                                                        border: 1,
                                                        borderColor: "divider",
                                                        borderRadius: 1,
                                                        mb: 1,
                                                        bgcolor:
                                                            selectedSegment ===
                                                                segment.id
                                                                ? "primary.light"
                                                                : "white",
                                                        cursor: "pointer",
                                                        "&:hover": {
                                                            bgcolor:
                                                                selectedSegment ===
                                                                    segment.id
                                                                    ? "primary.light"
                                                                    : "grey.50",
                                                        },
                                                    }}
                                                    onClick={() =>
                                                        setSelectedSegment(
                                                            segment.id,
                                                        )
                                                    }
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            flex: 1,
                                                            fontSize: "0.85rem",
                                                        }}
                                                    >
                                                        {segment.name}
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            bgcolor:
                                                                "primary.main",
                                                            borderRadius: 1,
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            mr: 1,
                                                        }}
                                                    >
                                                        {loading ? (
                                                            <CircularProgress
                                                                size={18}
                                                                sx={{
                                                                    mr: 1,
                                                                    color: "white",
                                                                }}
                                                            />
                                                        ) : (
                                                            <PlayArrow
                                                                onClick={async () =>
                                                                    getManifestUrl(
                                                                        segment,
                                                                    )
                                                                }
                                                                sx={{
                                                                    color: "white",
                                                                    fontSize: 16,
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleMenuOpen(
                                                                e,
                                                                segment,
                                                            );
                                                        }}
                                                        sx={{ p: 0.5 }}
                                                    >
                                                        <MoreVert fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Collapse>
                                </Box>
                            ))}
                        </Box>
                    </>
                )}
            </Paper>

            {/* Video Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleZoomToVideo}>
                    <ZoomIn fontSize="small" sx={{ mr: 1 }} />
                    Zoom to Video
                </MenuItem>
                <MenuItem onClick={handleShowDetails}>
                    <Visibility fontSize="small" sx={{ mr: 1 }} />
                    Show Details
                </MenuItem>
                <MenuItem onClick={handleDeleteVideo}>
                    <Delete fontSize="small" sx={{ mr: 1 }} />
                    Delete
                </MenuItem>
            </Menu>

            {/* Main Content Area */}
            <Paper
                variant="outlined"
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    borderWidth: 2,
                    minWidth: 0,
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        p: 1,
                        display: "flex",
                        justifyContent: drawerOpen ? "center" : "flex-start",
                        alignItems: "center",
                        height: "28px",
                        flexShrink: 0,
                        borderColor: "divider",
                        gap: 1,
                    }}
                >
                    {/* Toggle Button - Shows when drawer is closed */}
                    {!drawerOpen && (
                        <IconButton
                            size="small"
                            onClick={() => setDrawerOpen(true)}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                    <Typography variant="h6" fontWeight="600">
                        {getSelectedVideoInfo()}
                    </Typography>
                </Box>
                {/* Video Area */}

                {videourl && <VideoPlayer
                    mpdurl={videourl}
                    textTrackCallback={updateMarker}
                // mpdurl={"https://mrap-appdata.s3.ap-south-1.amazonaws.com/geospatial/survey-geotagged-videos/processed-geotagged-videos/16/manifest/master_playlist.mpd"}
                />}
            </Paper>

            {/* Resizer between main and right panels */}
            <Box
                onMouseDown={() => {
                    rightResizingRef.current = true;
                    document.body.style.userSelect = "none";
                }}
                onTouchStart={() => {
                    rightResizingRef.current = true;
                    document.body.style.userSelect = "none";
                }}
                sx={{
                    width: 8,
                    cursor: "ew-resize",
                    flexShrink: 0,
                    bgcolor: "transparent",
                    "&:hover": { bgcolor: "divider" },
                }}
            />

            {/* Persistent Right Panel (resizable) */}
            <Paper
                variant="outlined"
                sx={{
                    width: `${rightWidth}px`,
                    display: "flex",
                    flexDirection: "column",
                    borderWidth: 2,
                    flexShrink: 0,
                    overflow: "hidden",
                    transition: "width 0.05s linear",
                    marginLeft: "0.25rem",
                }}
            >
                <Box
                    sx={{
                        p: 1,
                        bgcolor: "primary.main",
                        color: "white",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        height: "28px",
                        flexShrink: 0,
                    }}
                >
                    Overview Map
                    {/* <IconButton size="small" sx={{ color: "white" }}>
                        <OpenInNewIcon fontSize="small" />
                    </IconButton> */}
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                    }}
                >
                    {/* Top: blank map / placeholder */}
                    <Box
                        ref={overviewMapRef}
                        sx={{
                            height: "50%",
                            borderBottom: 1,
                            borderColor: "divider",
                            margin: "5px"
                        }}
                    />

                    {/* Bottom: actual map target */}
                    <Box
                        ref={driveMapRef}
                        sx={{
                            height: "50%",
                            borderBottom: 1,
                            borderColor: "divider",
                            margin: "5px", position: "relative"
                        }}
                    >
                    </Box>


                    <NavigationIcon
                        ref={iconRef}
                        style={{
                            fontSize: 40,
                            color: 'red',
                            transition: 'transform 0.5s linear',
                        }}
                    />

                    <Box
                        ref={iconRef1}
                        sx={{
                            display: 'inline-block',
                            animation: 'blinkColor 1s infinite alternate ease-in-out',
                            '@keyframes blinkColor': {
                                from: { color: 'rgb(255, 4, 4)' }, // blue
                                to: { color: 'rgb(255, 184, 6)' },   // lighter blue
                            },
                        }}
                    >
                        <CircleIcon sx={{ fontSize: 12 }} /> {/* small size */}
                    </Box>

                </Box>
            </Paper >
        </Box >
    );
}
